import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { DateRange } from 'react-day-picker';
import { useFormContext } from 'react-hook-form';

import useOffers from 'api/useOffers';
import {
  ExtendedDateRange,
  ScheduleTimeFrame
} from 'common/contracts';
import { EScheduleType, OfferType } from 'constants/enums';
import dayjs from 'dayjs';
import { PlusCircle } from 'lucide-react';
import { getDurationTextFromMinutes } from 'utils/formattingUtil';
import { EToasterStatus, showToast } from 'utils/showToast';
import ScheduleAppearancesTable from 'views/Offers/components/Schedule/components/ScheduleAppearancesTable';

import Button from 'components/Button/Button';
import DatePicker from 'components/DatePicker/DatePicker';
import { DatePickerMode, DatePickerValue } from 'components/DatePicker/DatePicker.types';
import Input from 'components/Input/Input';
import RichText from 'components/RichText/RichText';
import Typography from 'components/Typography/Typography';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'components/ui/tabs';

import { BuyOneGetManyFormFields } from '../../BuyOneGetManyForm.consts';
import { SCHEDULE_TAB_TRIGGERS } from '../../BuyOneGetManyForm.consts';
import ScheduleStatusToggle from '../ScheduleStatusToggle';

interface IScheduleTabProps {
  buyOneGetManyId?: string;
  edit: boolean;
}

const ScheduleTab: React.FC<IScheduleTabProps> = ({
  buyOneGetManyId,
  edit
}): React.ReactElement => {
  const { watch, setValue } = useFormContext<BuyOneGetManyFormFields>();
  const schedule = watch('schedule');
  const isActive = watch('active');

  const { updateOffer } = useOffers({
    offerId: buyOneGetManyId,
    offerType: OfferType.BUY_ONE_GET_MANY
  });

  const [appearancesTableData, setAppearancesTableData] = useState<ScheduleTimeFrame[]>([]);
  const [editRangeDatesValues, setEditRangeDatesValues] = useState<ExtendedDateRange | null>(null);
  const [localDateRange, setLocalDateRange] = useState<DateRange>({ from: undefined, to: undefined });
  const [localNotes, setLocalNotes] = useState('');
  const [formattedDuration, setFormattedDuration] = useState<string | null>(null);

  const activeTab = useMemo((): string => {
    if (schedule?.permanent) {
      return EScheduleType.PERMANENT;
    }
    return EScheduleType.SCHEDULED;
  }, [schedule?.permanent]);

  useEffect(() => {
    const timeFramesData: ScheduleTimeFrame[] =
      schedule?.timeFrames?.map(
        (date: ScheduleTimeFrame, index: number) => ({
          id: date.id || `id-${index}`,
          startTime: date.startTime,
          endTime: date.endTime,
          notes: date.notes
        })
      ) || [];
    setAppearancesTableData(timeFramesData);
  }, [schedule?.timeFrames]);

  useEffect(() => {
    if (localDateRange.from && localDateRange.to) {
      const diffMinutes = dayjs(localDateRange.to).diff(dayjs(localDateRange.from), 'minute');
      const duration = getDurationTextFromMinutes(diffMinutes);
      setFormattedDuration(duration);
    } else {
      setFormattedDuration(null);
    }
  }, [localDateRange]);

  useEffect(() => {
    if (editRangeDatesValues) {
      const [startDate, endDate, notes] = editRangeDatesValues;
      setLocalDateRange({ from: startDate, to: endDate });
      setLocalNotes(notes || '');
    } else {
      setLocalDateRange({ from: undefined, to: undefined });
      setLocalNotes('');
    }
  }, [editRangeDatesValues]);

  const resetScheduleValues = useCallback((): void => {
    setLocalDateRange({ from: undefined, to: undefined });
    setEditRangeDatesValues(null);
    setLocalNotes('');
  }, []);

  const updateOfferStatus = useCallback(
    async (active: boolean): Promise<void> => {
      if (edit) {
        try {
          await updateOffer.mutateAsync({
            offerId: String(buyOneGetManyId),
            form: { active }
          });
          setValue('active', active, { shouldDirty: true });
        } catch {
          showToast({
            message: 'Error editing 1+Many',
            status: EToasterStatus.ERROR
          });
        }
      } else {
        setValue('active', active, { shouldDirty: true });
      }
    },
    [edit, updateOffer, buyOneGetManyId, setValue]
  );

  const handleTabChange = useCallback(
    (value: string): void => {
      const isPermanent = value === EScheduleType.PERMANENT;
      setValue(
        'schedule',
        {
          timeFrames: schedule?.timeFrames || [],
          permanent: isPermanent
        },
        { shouldDirty: true }
      );
      resetScheduleValues();
    },
    [schedule?.timeFrames, setValue, resetScheduleValues]
  );

  const handleApply = useCallback((): void => {
    const fromUtcDate = localDateRange.from;
    const toUtcDate = localDateRange.to;

    if (!fromUtcDate || !toUtcDate) {
      return;
    }

    const newTimeFrame: ScheduleTimeFrame = {
      startTime: new Date(fromUtcDate.toISOString()),
      endTime: new Date(toUtcDate.toISOString()),
      notes: localNotes
    };

    if (editRangeDatesValues) {
      const [editStartTime, editEndTime] = editRangeDatesValues;
      const editStartTimestamp = editStartTime.getTime();
      const editEndTimestamp = editEndTime.getTime();

      const updatedTimeFrames = schedule?.timeFrames?.map(
        (timeFrame: ScheduleTimeFrame) => {
          const currentStartTimestamp = new Date(timeFrame.startTime).getTime();
          const currentEndTimestamp = new Date(timeFrame.endTime).getTime();

          if (
            currentStartTimestamp === editStartTimestamp &&
            currentEndTimestamp === editEndTimestamp
          ) {
            return newTimeFrame;
          }
          return timeFrame;
        }
      );
      setValue(
        'schedule',
        { permanent: false, timeFrames: updatedTimeFrames },
        { shouldDirty: true }
      );
    } else {
      setValue(
        'schedule',
        {
          permanent: false,
          timeFrames: [...(schedule?.timeFrames || []), newTimeFrame]
        },
        { shouldDirty: true }
      );
    }

    resetScheduleValues();
  }, [localDateRange, localNotes, editRangeDatesValues, schedule?.timeFrames, setValue, resetScheduleValues]);

  const handleCancel = useCallback((): void => {
    resetScheduleValues();
  }, [resetScheduleValues]);

  const handleEditTimeframe = useCallback(
    (
      startTime: Date | string,
      endTime: Date | string,
      notes: string
    ): void => {
      const start = startTime instanceof Date ? startTime : new Date(startTime);
      const end = endTime instanceof Date ? endTime : new Date(endTime);
      setEditRangeDatesValues([start, end, notes]);
    },
    []
  );

  const handleDateRangeApply = useCallback(
    (value: DatePickerValue): void => {
      setLocalDateRange(value as DateRange);
    },
    []
  );

  const handleNotesChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      setLocalNotes(e.target.value);
    },
    []
  );

  const handleScheduleChange = useCallback(
    (newSchedule: { permanent?: boolean; timeFrames: ScheduleTimeFrame[] }): void => {
      setValue('schedule', {
        permanent: newSchedule.permanent ?? false,
        timeFrames: newSchedule.timeFrames
      }, { shouldDirty: true });
    },
    [setValue]
  );

  const isApplyDisabled = !localDateRange.from || !localDateRange.to;

  let buttonLabel = 'Add Appearance';
  if (editRangeDatesValues) {
    buttonLabel = 'Apply';
  }

  return (
    <div className="flex flex-col pb-10">
      <RichText
        title="Scheduled offer"
        description="Set specific dates & hours to your offer"
        className="flex flex-col gap-1.5 mb-6"
      />

      <ScheduleStatusToggle
        isActive={!!isActive}
        onToggle={updateOfferStatus}
        dataTestId="buyOneGetManyActiveSwitchBtn"
      />

      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <TabsList className="bg-zinc-100 p-1 rounded-xl grid grid-cols-2 h-10 w-full">
          {SCHEDULE_TAB_TRIGGERS.map((triggerTab) => (
            <TabsTrigger
              key={triggerTab.value}
              value={triggerTab.value}
              className="data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-zinc-900 text-zinc-500 rounded-lg font-normal h-8"
              data-testid={`buyOneGetManyScheduleTab-${triggerTab.value}`}
            >
              {triggerTab.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value={EScheduleType.PERMANENT} className="mt-6">
          <Typography className="text-sm text-zinc-500">
            Permanent events won&apos;t have an end date
          </Typography>
        </TabsContent>

        <TabsContent value={EScheduleType.SCHEDULED} className="mt-5">
          <div className="space-y-5">
            <DatePicker
              label="Pick A Date"
              labelSpacing="mb-2"
              shouldDisplayTime={true}
              shouldExcludePast={true}
              mode={DatePickerMode.RANGE}
              value={localDateRange}
              onApply={handleDateRangeApply}
            />

            <Input
              id="duration"
              label="Duration"
              value={formattedDuration || ''}
              placeholder="Select dates to calculate duration"
              disabled
            />

            <Input
              id="notes"
              label="Note"
              name="notes"
              value={localNotes}
              placeholder="Write something..."
              onChange={handleNotesChange}
            />

            <div className="space-y-3">
              {editRangeDatesValues && (
                <Button
                  onClick={handleCancel}
                  variant="outline"
                  className="w-full"
                >
                  Cancel
                </Button>
              )}
              <Button
                disabled={isApplyDisabled}
                onClick={handleApply}
                variant="secondary"
                className="w-full"
              >
                <PlusCircle className="w-4 h-4 mr-2" />
                {buttonLabel}
              </Button>
            </div>

            {appearancesTableData.length > 0 && (
              <ScheduleAppearancesTable
                schedule={{
                  permanent: schedule?.permanent,
                  timeFrames: schedule?.timeFrames || []
                }}
                onScheduleChange={handleScheduleChange}
                appearancesTableData={appearancesTableData}
                onEdit={handleEditTimeframe}
                onDelete={resetScheduleValues}
              />
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ScheduleTab;
